import {
  useState,
  useCallback,
  useRef,
  type CSSProperties,
  type MouseEvent,
} from "react";
import i18n from "i18next";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { usePhoneReadOnly } from "@/hooks/useMobileMode";
import { useCanEdit } from "@/lib/documentAccess";
import { useEditorStore } from "@/store/editorStore";
import { setDocumentDateLocale } from "@/lib/document-date";
import { BlockEditModal } from "../BlockEditModal";
import {
  formatDocumentDateDisplay,
  resolveDateMode,
  toDateInputValue,
  toLatexDateValue,
  type DocumentDateMode,
} from "@/lib/document-date";
import { TitleBlockView } from "../blocks/TitleBlockView";
import { useTitleLayout } from "../titleEdit/useTitleLayout";
import { TitleEditContent, type TitleEditValues } from "../titleEdit/TitleEditContent";

/**
 * Flow (TipTap) Title block — LaTeX \title/\author/\date (\maketitle).
 * Click opens a modal. Date: "Today" (live) or a fixed calendar day.
 */
export function TitleNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  // A reader (view only, or the phone's reading version) sees no "Author" stand-in (20 Oct).
  const showPlaceholders = useCanEdit() && !usePhoneReadOnly();
  // Dates in the document's language, as its PDF prints them (API: detectedLanguage is the language
  // the text is typeset in when it differs from the setting).
  const docLanguage = useEditorStore((s) => {
    const d = s.document as { language?: string; detectedLanguage?: string | null } | null;
    return d?.detectedLanguage || d?.language || "en";
  });
  setDocumentDateLocale(docLanguage);
  const title = (node.attrs.title as string) || "";
  const author = (node.attrs.author as string) || "";
  const date = (node.attrs.date as string) || "";
  const dateModeAttr = (node.attrs.dateMode as string) || "";
  const keywords = (node.attrs.keywords as string[] | undefined) ?? [];
  const acmCcs = (node.attrs.acmCcs as string | undefined) ?? "";
  const acmRights = (node.attrs.acmRights as string | undefined) ?? "";
  // ACM wants a country per author (the affiliation's last line): said in the modal (25 Oct 2026).
  const acm = useEditorStore((s) => ((s.document as { publisherProfile?: string | null } | null)?.publisherProfile ?? "").startsWith("acm-"));

  const { layout } = useTitleLayout();
  const sheet = layout === "sheet";

  const displayDate = formatDocumentDateDisplay(date, dateModeAttr);

  const [isOpen, setIsOpen] = useState(false);
  // What a Save would write, kept current by the modal body. It starts as the
  // stored strings, so saving without a change writes them back untouched.
  const draft = useRef<TitleEditValues>({ title, author });
  const [focusAuthor, setFocusAuthor] = useState<number | null>(null);
  const handleDraftChange = useCallback((v: TitleEditValues) => {
    draft.current = v;
  }, []);
  const [draftMode, setDraftMode] = useState<DocumentDateMode>(
    resolveDateMode(date, dateModeAttr),
  );
  const [draftDateIso, setDraftDateIso] = useState(
    toDateInputValue(date, dateModeAttr),
  );
  const [draftKeywords, setDraftKeywords] = useState(keywords.join(", "));
  const [draftCcs, setDraftCcs] = useState(acmCcs);
  const [draftRights, setDraftRights] = useState(acmRights);
  // Prevent click-through from dialog close re-opening the modal.
  const suppressOpenUntil = useRef(0);

  const closeModal = useCallback(() => {
    suppressOpenUntil.current = Date.now() + 400;
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (Date.now() < suppressOpenUntil.current) return;
      if (isOpen) return;
      draft.current = { title, author };
      // Clicking an author's name opens the modal on that author.
      const hit = (e.target as HTMLElement).closest?.('[data-testid="title-block-author-item"]');
      setFocusAuthor(hit ? Number(hit.getAttribute("data-index")) : null);
      setDraftMode(resolveDateMode(date, dateModeAttr));
      setDraftDateIso(toDateInputValue(date, dateModeAttr));
      setDraftKeywords(keywords.join(", "));
      setDraftCcs(acmCcs);
      setDraftRights(acmRights);
      setIsOpen(true);
    },
    [title, author, date, dateModeAttr, keywords, acmCcs, acmRights, isOpen],
  );

  const stopSelectOnly = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleSave = useCallback(() => {
    const nextDate =
      draftMode === "today"
        ? "\\today"
        : draftDateIso || toDateInputValue("", "today");
    updateAttributes({
      title: draft.current.title.trim(),
      author: draft.current.author,
      dateMode: draftMode,
      date: nextDate,
      keywords: draftKeywords.split(/[,;]/).map((k) => k.trim()).filter(Boolean),
      acmCcs: draftCcs,
      acmRights: draftRights,
    });
    closeModal();
  }, [draftMode, draftDateIso, draftKeywords, draftCcs, draftRights, updateAttributes, closeModal]);

  const latexDate = toLatexDateValue(date, dateModeAttr);

  return (
    <NodeViewWrapper
      data-type="title-block"
      data-block-id={node.attrs.blockId}
      data-testid="title-block"
      className={selected ? "ProseMirror-selectednode" : ""}
    >
      <TitleBlockView
        title={title}
        author={author}
        dateText={displayDate}
        dateHint={latexDate === "\\today" ? "Live date (\\today)" : `Fixed: ${latexDate}`}
        showPlaceholders={showPlaceholders}
        wrapperProps={{
          onMouseDown: stopSelectOnly,
          onClick: handleOpen,
          style: { cursor: "pointer" },
          title: "Click to edit title, author, and date",
        }}
      />

      <BlockEditModal
        sourceNode={node}
        title="Edit title page"
        open={isOpen}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={() => {
          closeModal();
          deleteNode();
        }}
        width={860}
        fixedWidth
        sheet={sheet}
      >
        <TitleEditContent
          title={title}
          author={author}
          focus={focusAuthor === null ? undefined : { author: focusAuthor }}
          onChange={handleDraftChange}
          onSubmit={handleSave}
          dateText={formatDocumentDateDisplay(draftMode === "today" ? "\\today" : draftDateIso, draftMode)}
          dateSlot={
            <>
              <label style={labelStyle}>Date</label>
              <div
                role="radiogroup"
                aria-label="Date mode"
                style={{ display: "flex", gap: 8, marginBottom: 10 }}
              >
                <ModeChip
                  active={draftMode === "today"}
                  onClick={() => setDraftMode("today")}
                  testId="title-modal-date-today"
                  label="Today"
                  hint="Updates every day (LaTeX \\today)"
                />
                <ModeChip
                  active={draftMode === "fixed"}
                  onClick={() => setDraftMode("fixed")}
                  testId="title-modal-date-fixed"
                  label="Pick a date"
                  hint="Fixed calendar day"
                />
              </div>
              {draftMode === "today" ? (
                <p style={{ ...hintStyle, marginTop: 0 }}>
                  Preview shows{" "}
                  <strong>{formatDocumentDateDisplay("\\today", "today")}</strong>.
                  Export emits <code>\date{"{\\today}"}</code>.
                </p>
              ) : (
                <input
                  data-testid="title-modal-date-input"
                  type="date"
                  value={draftDateIso}
                  onChange={(e) => setDraftDateIso(e.target.value)}
                  style={inputStyle}
                />
              )}
              <label style={{ ...labelStyle, marginTop: 14 }} htmlFor="title-modal-keywords">
                {i18n.t("editor:titleEdit.keywords")}
              </label>
              <input
                id="title-modal-keywords"
                data-testid="title-modal-keywords"
                value={draftKeywords}
                onChange={(e) => setDraftKeywords(e.target.value)}
                placeholder={i18n.t("editor:titleEdit.keywordsPlaceholder")}
                style={inputStyle}
              />
              <p style={hintStyle}>{i18n.t("editor:titleEdit.keywordsHint")}</p>
              {acm && (
                <>
                  <p style={hintStyle} data-testid="title-modal-acm-country">{i18n.t("editor:titleEdit.acmCountry")}</p>
                  {/* ACM's TAPS (25 Oct 2026): what ACM's tools give, pasted as is; the API keeps only those commands. */}
                  <label style={{ ...labelStyle, marginTop: 14 }} htmlFor="title-modal-acm-ccs">
                    {i18n.t("editor:titleEdit.acmCcs")}
                  </label>
                  <textarea
                    id="title-modal-acm-ccs"
                    data-testid="title-modal-acm-ccs"
                    value={draftCcs}
                    onChange={(e) => setDraftCcs(e.target.value)}
                    rows={4}
                    spellCheck={false}
                    style={{ ...inputStyle, fontFamily: "var(--lilia-font-mono, monospace)", fontSize: 12, resize: "vertical" }}
                  />
                  <p style={hintStyle}>{i18n.t("editor:titleEdit.acmCcsHint")}</p>
                  <label style={{ ...labelStyle, marginTop: 14 }} htmlFor="title-modal-acm-rights">
                    {i18n.t("editor:titleEdit.acmRights")}
                  </label>
                  <textarea
                    id="title-modal-acm-rights"
                    data-testid="title-modal-acm-rights"
                    value={draftRights}
                    onChange={(e) => setDraftRights(e.target.value)}
                    rows={4}
                    spellCheck={false}
                    style={{ ...inputStyle, fontFamily: "var(--lilia-font-mono, monospace)", fontSize: 12, resize: "vertical" }}
                  />
                  <p style={hintStyle}>{i18n.t("editor:titleEdit.acmRightsHint")}</p>
                </>
              )}
            </>
          }
        />
      </BlockEditModal>
    </NodeViewWrapper>
  );
}

function ModeChip({
  active,
  onClick,
  label,
  hint,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active}
      onClick={onClick}
      title={hint}
      style={{
        flex: 1,
        padding: "8px 10px",
        borderRadius: 8,
        border: active
          ? "1.5px solid var(--lilia-primary, #0A76A4)"
          : "1px solid var(--lilia-border, #ddd)",
        background: active
          ? "var(--lilia-primary-subtle, rgba(10,118,164,0.08))"
          : "var(--lilia-surface, #fff)",
        color: "var(--lilia-text)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 4,
  color: "var(--lilia-text)",
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  border: "1px solid var(--lilia-border, #ddd)",
  borderRadius: 6,
  boxSizing: "border-box",
};

const hintStyle: CSSProperties = {
  margin: "6px 0 0",
  fontSize: 11,
  color: "var(--lilia-text-muted, #666)",
  lineHeight: 1.4,
};
